import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowRight } from "lucide-react";

const SOCIALS = [
  { id: "google", label: "Continue with Google", color: "#fff", text: "#0F172A", border: "#E2E8F0", iconBg: "linear-gradient(135deg,#4285F4 0%,#EA4335 50%,#FBBC05 100%)" },
  { id: "apple", label: "Continue with Apple", color: "#000", text: "#fff" },
  { id: "linkedin", label: "Continue with LinkedIn", color: "#0A66C2", text: "#fff" },
];

function SocialBtn({ s }) {
  return (
    <button
      type="button"
      onClick={() => toast.info(`${s.id.charAt(0).toUpperCase() + s.id.slice(1)} login coming soon`)}
      data-testid={`social-${s.id}`}
      className="relative h-12 rounded-xl font-medium flex items-center justify-center gap-2 w-full transition-opacity hover:opacity-90 disabled:opacity-50"
      style={{ background: s.color, color: s.text, border: s.border ? `1.5px solid ${s.border}` : "none" }}
    >
      <span className="text-sm">{s.label}</span>
      <span className="absolute right-3 top-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold">Soon</span>
    </button>
  );
}

export default function LoginPage() {
  const { login, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid email or password");
    }
  };

  return (
    <div data-testid="login-page" className="min-h-[calc(100vh-160px)] flex items-center py-10">
      <div className="max-w-md mx-auto w-full px-5">
        <div className="text-center mb-8">
          <h1 className="dv-display text-3xl md:text-4xl mb-2">Welcome back</h1>
          <p className="text-slate-500">Log in to your DealVest account</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 dv-shadow-soft">
          <div className="space-y-2.5 mb-5">
            {SOCIALS.map(s => <SocialBtn key={s.id} s={s} />)}
          </div>
          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs uppercase tracking-wider text-slate-400 font-medium">or with email</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>
          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="dv-label">Email</label>
              <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="dv-input" placeholder="you@example.com" data-testid="login-email" />
            </div>
            <div>
              <label className="dv-label">Password</label>
              <div className="relative">
                <input type={show ? "text" : "password"} required value={password} onChange={e => setPassword(e.target.value)} className="dv-input pr-12" placeholder="••••••••" data-testid="login-password" />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">{show ? <EyeOff size={18} /> : <Eye size={18} />}</button>
              </div>
            </div>
            <button type="submit" disabled={loading} className="dv-btn dv-btn-primary w-full" data-testid="login-submit">
              {loading ? "Logging in..." : "Log in"} <ArrowRight size={16} />
            </button>
          </form>
          <p className="text-center text-sm text-slate-500 mt-6">
            Don't have an account? <Link to="/signup" className="text-blue-600 font-semibold">Sign up free</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
